/* eslint-disable react/prop-types */
import { Formik, Form } from "formik";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Import component UI form cho Sổ Chi Tiết Thuế GTGT Được Hoàn Lại (S62-DN)
import ValueAddedTaxRefundInfo from "./info";

import { ValueAddedTaxRefundService } from "services/value-added-tax-refund";
import { getPrintTemplate } from "./components/printTemplate";

const today = new Date();

// 1. Initial values khớp DTO CreateValueAddedTaxRefundDto
const initialValues = {
  company: "",
  companyAddress: "",
  year: today.getFullYear().toString(),
  openingToBeRefundedAmount: "",
  openingAlreadyRefundedAmount: "",
  items: [],
  totalArisingToBeRefundedAmount: "",
  totalArisingAlreadyRefundedAmount: "",
  closingToBeRefundedAmount: "",
  closingAlreadyRefundedAmount: "",
  totalPages: "",
  openingDate: "",
  signingDay: today.getDate().toString(),
  signingMonth: (today.getMonth() + 1).toString(),
  signingYear: today.getFullYear().toString(),
  preparerName: "",
  chiefAccountantName: "",
  legalRepresentativeName: "",
  status: "OPEN",
};

function ValueAddedTaxRefundDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();
  const [getData, setGetData] = useState({});
  const [data, setData] = useState({});

  const [formInitials, setFormInitials] = useState(initialValues);

  // 2. Load data từ API và map vào form
  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const response = await ValueAddedTaxRefundService.getById(id);
        const apiData = response.data ? response.data : response;

        // Map items: VatRefundTransaction schema
        const mappedItems = (apiData.items || []).map((item) => ({
          documentNumber: item.documentNumber || "",
          documentDate: item.documentDate
            ? new Date(item.documentDate).toLocaleDateString("vi-VN")
            : "",
          description: item.description || "",
          toBeRefundedAmount: item.toBeRefundedAmount || "",
          alreadyRefundedAmount: item.alreadyRefundedAmount || "",
        }));

        // Parse signingDate
        let sDay = initialValues.signingDay;
        let sMonth = initialValues.signingMonth;
        let sYear = initialValues.signingYear;
        if (apiData.signingDate) {
          const sDate = new Date(apiData.signingDate);
          sDay = sDate.getDate().toString();
          sMonth = (sDate.getMonth() + 1).toString();
          sYear = sDate.getFullYear().toString();
        }

        const loadedData = {
          ...initialValues,
          company: apiData.company?.name || apiData.company || "",
          companyAddress: apiData.companyAddress || "",
          year: apiData.year?.toString() || initialValues.year,
          openingToBeRefundedAmount: apiData.openingToBeRefundedAmount || "",
          openingAlreadyRefundedAmount: apiData.openingAlreadyRefundedAmount || "",
          items: mappedItems.length > 0 ? mappedItems : [],
          totalArisingToBeRefundedAmount: apiData.totalArisingToBeRefundedAmount || "",
          totalArisingAlreadyRefundedAmount: apiData.totalArisingAlreadyRefundedAmount || "",
          closingToBeRefundedAmount: apiData.closingToBeRefundedAmount || "",
          closingAlreadyRefundedAmount: apiData.closingAlreadyRefundedAmount || "",
          totalPages: apiData.totalPages || "",
          openingDate: apiData.openingDate
            ? new Date(apiData.openingDate).toLocaleDateString("vi-VN")
            : "",
          signingDay: sDay,
          signingMonth: sMonth,
          signingYear: sYear,
          preparerName: apiData.preparerName || "",
          chiefAccountantName: apiData.chiefAccountantName || "",
          legalRepresentativeName: apiData.legalRepresentativeName || "",
          status: apiData.status || "OPEN",
        };

        setFormInitials(loadedData);
        setGetData(apiData);
      } catch (error) {
        console.error("Lỗi getById Sổ GTGT Hoàn Lại:", error);
        toast.error("Không thể tải dữ liệu");
      }
    })();
  }, [id]);

  const onChangeValue = useCallback((type, value) => {
    setData((prev) => ({ ...prev, [type]: value }));
  }, []);

  const handleBack = () => navigation(-1);

  // 3. Submit payload 100% khớp CreateValueAddedTaxRefundDto
  const submitForm = async (values) => {
    try {
      const num = (val) => (val && !isNaN(val) ? Number(val) : 0);

      const getValue = (field) => (data[field] !== undefined ? data[field] : values[field]);
      const itemsFlat = getValue("items") || [];

      // Build items theo VatRefundTransaction schema
      const buildItems = (itemsList) =>
        itemsList.map((item) => ({
          documentNumber: item.documentNumber || "",
          documentDate: item.documentDate ? new Date(item.documentDate) : null,
          description: item.description || "",
          toBeRefundedAmount: num(item.toBeRefundedAmount),
          alreadyRefundedAmount: num(item.alreadyRefundedAmount),
        }));

      // Build signingDate ISO
      const signD = getValue("signingDay");
      const signM = getValue("signingMonth");
      const signY = getValue("signingYear");
      let signingDate = null;
      if (signY && signM && signD) {
        signingDate = new Date(
          `${signY}-${String(signM).padStart(2, "0")}-${String(signD).padStart(2, "0")}`
        ).toISOString();
      }

      // Payload chính xác theo DTO
      const payload = {
        companyAddress: getValue("companyAddress") || "",
        year: num(getValue("year")),
        openingToBeRefundedAmount: num(getValue("openingToBeRefundedAmount")),
        openingAlreadyRefundedAmount: num(getValue("openingAlreadyRefundedAmount")),
        items: buildItems(itemsFlat),
        totalPages: num(getValue("totalPages")),
        openingDate: getValue("openingDate") ? new Date(getValue("openingDate")) : null,
        signingDate,
        preparerName: getValue("preparerName") || "",
        chiefAccountantName: getValue("chiefAccountantName") || "",
        legalRepresentativeName: getValue("legalRepresentativeName") || "",
        status: getValue("status") || "OPEN",
      };

      console.log("✅ ValueAddedTaxRefund Payload:", JSON.stringify(payload, null, 2));

      if (!id) {
        await ValueAddedTaxRefundService.create(payload);
        toast.success("Tạo Sổ Chi Tiết GTGT Được Hoàn Lại thành công!");
      } else {
        await ValueAddedTaxRefundService.update(id, payload);
        toast.success("Cập nhật thành công!");
      }
    } catch (error) {
      console.error("Lỗi lưu:", error);
      toast.error(error?.response?.data?.message || "Lưu thất bại!");
      throw error;
    }
  };

  const handlePrintDirect = (values) => {
    const finalData = { ...values, ...data };
    const htmlContent = getPrintTemplate({
      headerInfo: finalData,
      rows: finalData.items || [],
      totals: {
        totalArisingToBeRefundedAmount: finalData.totalArisingToBeRefundedAmount,
        totalArisingAlreadyRefundedAmount: finalData.totalArisingAlreadyRefundedAmount,
        closingToBeRefundedAmount: finalData.closingToBeRefundedAmount,
        closingAlreadyRefundedAmount: finalData.closingAlreadyRefundedAmount,
      },
    });

    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const handleSubmit = async (values, actions) => {
    try {
      await submitForm(values);
    } catch (error) {
      // Error đã handle trong submitForm
    } finally {
      if (actions) {
        actions.setTouched({});
        actions.setSubmitting(false);
      }
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={20}>
        <Grid container justifyContent="center" sx={{ height: "100%" }}>
          <Grid item xs={12} lg={12}>
            <Formik initialValues={formInitials} enableReinitialize={true} onSubmit={handleSubmit}>
              {({ values, errors, touched, isSubmitting, setFieldValue }) => (
                <Form autoComplete="off">
                  <Card sx={{ height: "100%" }}>
                    <SoftBox p={2}>
                      <SoftBox>
                        <ValueAddedTaxRefundInfo
                          formData={{
                            values,
                            touched,
                            errors,
                            getData,
                            onChangeValue,
                            setFieldValue,
                          }}
                        />

                        <SoftBox mt={10} width="100%" display="flex" justifyContent="space-between">
                          <SoftButton variant="gradient" color="light" onClick={handleBack}>
                            Trở về
                          </SoftButton>

                          {containView ? (
                            <SoftBox display="flex" gap={3}>
                              <SoftButton
                                variant="gradient"
                                color="success"
                                onClick={() => handlePrintDirect(values)}
                                type="button"
                              >
                                In Sổ Chi Tiết (S62-DN)
                              </SoftButton>
                            </SoftBox>
                          ) : (
                            <SoftBox display="flex" gap={3}>
                              <SoftButton
                                disabled={isSubmitting}
                                type="submit"
                                variant="gradient"
                                color="dark"
                              >
                                {id ? "Lưu" : "Tạo"}
                              </SoftButton>
                            </SoftBox>
                          )}
                        </SoftBox>
                      </SoftBox>
                    </SoftBox>
                  </Card>
                </Form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}

export default ValueAddedTaxRefundDetail;
